/**
 * @author      yewon@zumper.com (Yewon Lee)
 * @copyright   Copyright (c) 2018, Zumper
 * @description Meta data reducer for the simialrListings
 */

import { nullReducer } from '@zcode/reducers'
import type { NRState } from '@zcode/state-type'
import type { Values } from '@zcode/utility-types'
import { type Reducer, combineReducers } from 'redux'
import { type ExtractActions, handleActions } from 'redux-actions'
import invariant from 'tiny-invariant'
import type * as actions from '../actions'
import {
  CLEAR_LISTABLES,
  DONE_FETCHING,
  ERROR_FETCHING,
  RECEIVE_LISTABLES,
  START_FETCHING,
} from '../constants'

type Actions = ExtractActions<Values<typeof actions>>
type Meta = NRState['similarListings']['meta']

const addResolve = (
  promise: Promise<void>,
  resolve: () => void
): Promise<void> & { resolve: () => void } => {
  const p = promise as Promise<void> & { resolve: () => void }
  p.resolve = resolve
  return p
}

const metaReducer: Reducer<any, Actions> = combineReducers<Meta, Actions>({
  currentKey: handleActions(
    {
      [CLEAR_LISTABLES]: nullReducer,
      [ERROR_FETCHING]: nullReducer,
      [RECEIVE_LISTABLES]: (state, action) => {
        const { payload } = action
        if (payload?.key) {
          return payload.key
        }
        return state
      },
    },
    null as string | null
  ),
  fetchingKey: handleActions(
    {
      [DONE_FETCHING]: nullReducer,
      [ERROR_FETCHING]: nullReducer,
      [START_FETCHING]: (state, action) => (action?.payload ? action.payload : state),
    },
    null as string | null
  ),
  fetchingPromise: handleActions(
    {
      [DONE_FETCHING]: (state) => {
        state?.resolve()
        return null
      },
      [ERROR_FETCHING]: (state) => {
        state?.resolve()
        return null
      },
      // eslint-disable-next-line @typescript-eslint/promise-function-async
      [START_FETCHING]: () => {
        let r: (() => void) | undefined
        const promise = new Promise<void>((resolve) => {
          r = resolve
        })
        invariant(r)
        return addResolve(promise, r)
      },
    },
    null as (Promise<void> & { resolve: () => void }) | null
  ),
  groupId: handleActions(
    {
      [CLEAR_LISTABLES]: nullReducer,
      [START_FETCHING]: nullReducer,
      [RECEIVE_LISTABLES]: (state, action) => {
        const { payload } = action
        return payload.groupId ?? null
      },
    },
    null as NRState['similarListings']['meta']['groupId']
  ),
  source: handleActions(
    {
      [CLEAR_LISTABLES]: nullReducer,
      [START_FETCHING]: nullReducer,
      [RECEIVE_LISTABLES]: (state, action) => {
        const { payload } = action
        return payload.source ?? null
      },
    },
    null as NRState['similarListings']['meta']['source']
  ),
})

export default metaReducer
